import { Routes } from '@angular/router';
import { PublicLayoutComponent } from './layout/public-layout/public-layout.component';
import { ProtectedLayoutComponent } from './layout/protected-layout/protected-layout.component';
import { authGuard } from './auth/guards/auth.guard';
import { PermissionGuard } from './auth/guards/permission.guard';
import { affiliatePortalAuthGuard } from './affiliate-portal/guards/affiliate-portal-auth.guard';
import { partnerHostGuard, masterHostGuard } from './affiliate-portal/guards/partner-host.guard';


export const routes: Routes = [
  {
    path: '',
    pathMatch: 'full',
    redirectTo: 'home',
  },
  // Public Routes (master dashboard — never matched on a partner host)
  {
    path: '',
    component: PublicLayoutComponent,
    canMatch: [masterHostGuard],
    children: [
      {
        path: 'login',
        title: 'ATS App Core | Login',
        loadComponent: () =>
          import('./auth/pages/login/login.component').then(m => m.LoginComponent),
      },
      {
        path: 'forgot-password',
        title: 'ATS App Core | Forgot Password',
        loadComponent: () =>
          import('./auth/pages/forgot-password/forgot-password.component')
            .then(m => m.ForgotPasswordComponent),
      },
      {
        path: 'change-password',
        title: 'ATS App Core | Change Password',
        loadComponent: () =>
          import('./auth/pages/change-password/change-password.component')
            .then(m => m.ChangePasswordComponent),
      }
    ]
  },
  // Affiliate portal — selected by HOST, not path. When the app loads on
  // `{affiliation}.partner.atsmako.{tld}` (or with the non-prod `?affiliation=`
  // override), `partnerHostGuard` lets these root routes match and the
  // affiliation is read from the host via AffiliateHostService. On any other
  // host they're skipped and the master routes below (guarded by
  // `masterHostGuard`) match instead, so the two never collide on shared paths
  // like `login` / `home` / `offices/:id`.
  {
    path: '',
    canMatch: [partnerHostGuard],
    loadComponent: () =>
      import('./affiliate-portal/layout/public-layout/public-layout.component')
        .then(m => m.AffiliatePortalPublicLayoutComponent),
    children: [
      {
        path: 'login',
        title: 'Partner Portal | Sign In',
        loadComponent: () =>
          import('./affiliate-portal/pages/login/login.component')
            .then(m => m.AffiliatePortalLoginComponent),
      },
    ],
  },
  {
    path: '',
    canMatch: [partnerHostGuard, affiliatePortalAuthGuard],
    loadComponent: () =>
      import('./affiliate-portal/layout/protected-layout/protected-layout.component')
        .then(m => m.AffiliatePortalProtectedLayoutComponent),
    children: [
      {
        path: 'home',
        title: 'Partner Portal | Home',
        loadComponent: () =>
          import('./affiliate-portal/pages/home/home.component')
            .then(m => m.AffiliatePortalHomeComponent),
      },
      {
        path: 'my-offices',
        title: 'Partner Portal | My Offices',
        loadComponent: () =>
          import('./affiliate-portal/pages/my-offices/my-offices.component')
            .then(m => m.AffiliatePortalMyOfficesComponent),
      },
      {
        path: 'offices/:id',
        title: 'Partner Portal | Office',
        loadComponent: () =>
          import('./affiliate-portal/pages/office-detail/office-detail.component')
            .then(m => m.AffiliatePortalOfficeDetailComponent),
      },
      {
        path: 'users',
        title: 'Partner Portal | Users',
        loadComponent: () =>
          import('./affiliate-portal/pages/users/users.component')
            .then(m => m.AffiliatePortalUsersComponent),
      },
      {
        path: 'reports',
        pathMatch: 'full',
        redirectTo: 'reports/recruiting',
      },
      {
        path: 'reports/:tab',
        title: 'Partner Portal | Reports',
        loadComponent: () =>
          import('./affiliate-portal/pages/reports/reports-page.component')
            .then(m => m.AffiliatePortalReportsPageComponent),
      },
    ],
  },
  // Protected Routes (master dashboard — never matched on a partner host)
  {
    path: '',
    component: ProtectedLayoutComponent,
    canMatch: [masterHostGuard, authGuard],
    children: [
      {
        path: 'home',
        title: 'ATS App Core | Home',
        data: { permission: 'home' },
        canActivate: [PermissionGuard],
        loadComponent: () =>
          import('./home/pages/home-page/home-page.component')
            .then(m => m.HomePageComponent),
      },
      {
        path: 'prospects',
        pathMatch: 'full',
        redirectTo: 'prospects/prospects',
      },
      {
        path: 'prospects/:tab',
        title: 'ATS App Core | Prospects',
        data: { permission: 'prospects' },
        canActivate: [PermissionGuard],
        loadComponent: () =>
          import('./invited/pages/prospects-page/prospects-page.component')
            .then(m => m.ProspectsPageComponent),
      },
      {
        path: 'accounts',
        pathMatch: 'full',
        redirectTo: 'accounts/offices',
      },
      {
        path: 'accounts/:tab',
        title: 'ATS App Core',
        data: { permission: 'accounts' },
        canActivate: [PermissionGuard],
        loadComponent: () =>
          import('./accounts/pages/accounts-page/accounts-page.component').then(m => m.AccountsPageComponent),
      },
      {
        path: 'reports',
        title: 'ATS App Core | Reports',
        data: { permission: 'reports' },
        canActivate: [PermissionGuard],
        loadComponent: () =>
          import('./reports/pages/reports-page/reports-page.component')
            .then(m => m.ReportsPageComponent),
      },
      {
        path: 'user_accounts',
        title: 'ATS App Core | User Accounts',
        data: { permission: 'user_accounts' },
        canActivate: [PermissionGuard],
        loadComponent: () =>
          import('./users/pages/user-accounts/user-accounts.component')
            .then(m => m.UserAccountsComponent),
      },
      {
        path: 'admins',
        title: 'ATS App Core | Users Management',
        data: { permission: 'admins' },
        canActivate: [PermissionGuard],
        loadComponent: () =>
          import('./users/pages/admins/admins-page.component')
            .then(m => m.AdminsPageComponent),
      },
      {
        path: 'communication',
        pathMatch: 'full',
        redirectTo: 'communication/emails',
      },
      {
        path: 'communication/:tab',
        title: 'ATS App Core  | Communication',
        data: { permission: 'communication' },
        canActivate: [PermissionGuard],
        loadComponent: () =>
          import('./communication/pages/communication-page/communication-page.component')
            .then(m => m.CommunicationPageComponent),
      },
      {
        path: 'tasks',
        title: 'ATS App Core | Tasks',
        data: { permission: 'tasks' },
        canActivate: [PermissionGuard],
        loadComponent: () =>
          import('./tasks/pages/tasks-page/tasks-page.component').then(m => m.TasksPageComponent),
      },
      {
        path: 'offices/:id',
        title: 'ATS App Core | Office',
        data: { permission: 'accounts' },
        canActivate: [PermissionGuard],
        loadComponent: () =>
          import('./offices/pages/office-dashboard/office-dashboard.component')
            .then(m => m.OfficeDashboardComponent),
      },
      {
        path: 'support',
        pathMatch: 'full',
        redirectTo: 'support/inbox',
      },
      {
        path: 'support/inbox',
        title: 'ATS App Core | Support Inbox',
        data: { tab: 'inbox', permission: 'support' },
        canActivate: [PermissionGuard],
        loadComponent: () =>
          import('./support/pages/support-page/support-page.component')
            .then(m => m.SupportPageComponent),
      },
      {
        path: 'support/escalations',
        title: 'ATS App Core | Escalation Tracker',
        data: { tab: 'tracker', permission: 'support' },
        canActivate: [PermissionGuard],
        loadComponent: () =>
          import('./support/pages/support-page/support-page.component')
            .then(m => m.SupportPageComponent),
      },
      {
        path: 'legal',
        title: 'ATS App Core | Legal',
        data: { permission: 'legal' },
        canActivate: [PermissionGuard],
        loadComponent: () =>
          import('./legal/pages/legal-page/legal-page.component')
            .then(m => m.LegalPageComponent),
      },
      {
        path: 'features',
        title: 'ATS App Core | Features',
        data: { permission: 'features' },
        canActivate: [PermissionGuard],
        loadComponent: () =>
          import('./features/pages/features-page/features-page.component')
            .then(m => m.FeaturesPageComponent),
      },
      {
        path: 'mimi',
        pathMatch: 'full',
        redirectTo: 'mimi/dashboard',
      },
      {
        path: 'mimi/:tab',
        title: 'ATS App Core | Mimi',
        data: { permission: 'mimi' },
        canActivate: [PermissionGuard],
        loadComponent: () =>
          import('./mimi/pages/mimi-page/mimi-page.component')
            .then(m => m.MimiPageComponent),
      },
      {
        path: 'configurations',
        pathMatch: 'full',
        redirectTo: 'configurations/support-email',
      },
      {
        path: 'configurations/:tab',
        title: 'ATS App Core | Configurations',
        data: { permission: 'configurations' },
        canActivate: [PermissionGuard],
        loadComponent: () =>
          import('./configurations/pages/configurations-page/configurations-page.component')
            .then(m => m.ConfigurationsPageComponent),
      },
      {
        path: 'inputs',
        title: 'ATS App Core | Inputs',
        data: { permission: 'inputs' },
        canActivate: [PermissionGuard],
        loadComponent: () =>
          import('./inputs/pages/inputs-page/inputs-page.component')
            .then(m => m.InputsPageComponent),
      },
      {
        path: 'ai_social',
        pathMatch: 'full',
        redirectTo: 'ai_social/voice-brand',
      },
      {
        path: 'ai_social/:tab',
        title: 'ATS App Core | AI Social',
        data: { permission: 'ai_social' },
        canActivate: [PermissionGuard],
        loadComponent: () =>
          import('./ai-social/pages/ai-social-page/ai-social-page.component')
            .then(m => m.AiSocialPageComponent),
      },
      {
        path: '**',
        title: 'ATS App Core | Coming Soon',
        loadComponent: () =>
          import('./shared/components/coming-soon/coming-soon.component')
            .then(m => m.ComingSoonComponent),
      },
    ]
  },
  {
    path: '**',
    title: 'ATS App Core | Not Found',
    loadComponent: () =>
      import('./shared/components/not-found/not-found.component').then(m => m.NotFoundComponent),
  },
];
